import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { driver, Driver, DriveStep } from 'driver.js';

import { AuthManagementService } from '@core/service/auth-management.service';
import { TourUiBridgeService } from '@core/service/tour-ui-bridge.service';

import { buildGeneralHeaderSteps, buildAdminAdvancedFilterSteps, buildAdminViewRecipesSteps, buildAdminOnboardingClientSteps,
 buildAdminCrearSucursalSteps, buildAdminCrearGrupoEmpresarialSteps, buildAdminCrearPersonalSteps,
buildAdminCrearMedicoSteps, buildDoctorCrearPacienteSteps, buildPacienteEventosSaludSteps, buildStaffSurtirRecetaSteps,
  buildPacienteVerRecetasYMedsSteps, buildDoctorCrearRecetaSteps, buildDoctorEditarRecetaSteps} from "@core/service/guided-tour/guided-tour.steps";


export type RmdTourId =
  | 'tutorial_general'
  | 'admin_primeros_pasos'
  | 'admin_filtro_avanzado'
  | 'admin_ver_recetas'
  | 'admin_crear_personal'
  | 'admin_crear_sucursales'
  | 'admin_crear_grupo_empresarial'
  | 'admin_crear_medico'
  | 'doctor_crear_paciente'
  | 'doctor_crear_receta'
  | 'doctor_editar_receta'
  | 'staff_surtir_receta'
  | 'paciente_mis_recetas_medicamentos'
  | 'paciente_eventos_salud';

type TourDef = {
  id: RmdTourId;
  title: string;
  description?: string;
  isEnabled: () => boolean;
  start: (opts?: { force?: boolean }) => void;
};

@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  private _driver: Driver | null = null;
  private lastStepIndexReached = -1;
  private progressObserver: MutationObserver | null = null;

  private readonly TOUR_VERSION = 'v1';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private authManagement: AuthManagementService,
    private tourUi: TourUiBridgeService
  ) {}

  // =========================
  // PUBLIC (Menu)
  // =========================

  public getAvailableTours(): Array<Pick<TourDef, 'id' | 'title' | 'description'>> {
    return this.getCatalog()
      .filter(t => t.isEnabled())
      .map(({ id, title, description }) => ({ id, title, description }));
  }

  public startTour(id: RmdTourId, opts?: { force?: boolean }): void {
    const tour = this.getCatalog().find(t => t.id === id);
    if (!tour) return;
    tour.start(opts);
  }

  // =========================
  // Catalogo
  // =========================

  private getCatalog(): TourDef[] {
    return [
      // 1) Tutorial general (TODOS)
      {
        id: 'tutorial_general',
        title: 'TUTORIAL GENERAL',
        description: 'Recorrido general del sistema (header + menu).',
        isEnabled: () => true, // ✅ lo pueden ver todos
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('tutorial_general')) return;

          this.ensureRoute('/dashboard', () => this.runGeneralHeaderTour());
        },
      },

      // 2) Primeros pasos (admin)
      {
        id: 'admin_primeros_pasos',
        title: 'ALTA DE CLIENTE',
        description: 'Que hacer cuando llega un cliente nuevo: Grupo → Sucursal → Personal → Credenciales.',
        isEnabled: () => this.isAdmin(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_primeros_pasos')) return;

          this.ensureRoute('/dashboard', () => this.runAdminPrimerosPasosTour());
        },
      },

      // 3) Filtro avanzado (admin)
      {
        id: 'admin_filtro_avanzado',
        title: 'FILTRO AVANZADO',
        description: 'Uso de GRUPO y SUCURSAL desde el menu de cuenta.',
        isEnabled: () => this.canSeeBranchFilterTours(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_filtro_avanzado')) return;

          const startRoute = this.getAdvancedFilterStartRoute();
          this.ensureRoute(startRoute, () => this.runSuperAdminAdvancedFilterTour());
        },
      },
      // 4) Ver recetas (admin)
      {
        id: 'admin_ver_recetas',
        title: 'VER RECETAS',
        description: 'Como buscar recetas usando filtros.',
        isEnabled: () => this.canSeeDoctorTours(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_ver_recetas')) return;

          this.ensureRoute('/prescription/prescription-list', () => this.runSuperAdminViewRecipesTour());
        },
      },

      // 5) Crear Personal (ADMIN + SUPERVISOR + RESPONSABLE FARMACIA)
      {
        id: 'admin_crear_personal',
        title: 'CREAR PERSONAL',
        description: 'Alta de usuarios del sistema (personal).',
        isEnabled: () => this.canSeeCrearPersonalTour(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_crear_personal')) return;

          this.ensureRoute('/admin/user-list', () => this.runCrearPersonalTour());
        },
      },

      // 6) Crear Sucursales (admin)
      {
        id: 'admin_crear_sucursales',
        title: 'CREAR SUCURSALES',
        description: 'Alta y gestion de sucursales.',
        isEnabled: () => this.canSeeBranchFilterTours(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_crear_sucursales')) return;

          this.ensureRoute('/branch/branch-list', () => this.runCrearSucursalesTour());
        },
      },

      // 7) Crear Grupo empresarial (admin)
      {
        id: 'admin_crear_grupo_empresarial',
        title: 'CREAR GRUPO EMPRESARIAL',
        description: 'Alta y configuracion de grupos empresariales.',
        isEnabled: () => this.isAdmin(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_crear_grupo_empresarial')) return;

          this.ensureRoute('/catalogs/catalogs-list-v2', () => this.runCrearGrupoEmpresarialTour());
        },
      },

      // 8) Crear Medico (admin)
      {
        id: 'admin_crear_medico',
        title: 'CREAR MEDICO',
        description: 'Alta y gestion de medicos.',
        isEnabled: () => this.isAdmin() || this.isSupervisor() || this.isResponsable(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('admin_crear_medico')) return;

          this.ensureRoute('/doctor/doctor-list', () => this.runCrearMedicoTour());
        },
      },
      {
        id: 'doctor_crear_paciente',
        title: 'CREAR PACIENTE',
        description: 'Alta de paciente (solo MEDICO).',
        isEnabled: () => this.canSeeDoctorTours(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('doctor_crear_paciente')) return;

          this.ensureRoute('/patient/patient-list', () => this.runCrearPacienteTour());
        },
      },
      {
        id: 'doctor_crear_receta',
        title: 'CREAR RECETA',
        description: 'Proceso guiado para crear una receta (MEDICO y PARTICULAR).',
        isEnabled: () => this.canSeeDoctorTours(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('doctor_crear_receta')) return;

          this.ensureRoute('/prescription/tutorial-create', () => this.runCrearRecetaTour());
        },
      },
      {
        id: 'doctor_editar_receta',
        title: 'EDITAR RECETA',
        description: 'Modificacion de una receta existente.',
        isEnabled: () => this.canSeeDoctorTours(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('doctor_editar_receta')) return;

          this.ensureRoute('/prescription/tutorial-edit', () => this.runEditarRecetaTour());
        },
      },
      {
        id: 'staff_surtir_receta',
        title: 'SURTIR RECETA',
        description: 'Como surtir una receta por folio o QR.',
        isEnabled: () => this.isEmpleado() || this.isResponsable() || this.isSupervisor(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('staff_surtir_receta')) return;
          this.ensureRoute('/prescription/prescription-supply', () => this.runStaffSurtirRecetaTour());
        },
      },
      // PACIENTE · Mis recetas + medicamentos
      {
        id: 'paciente_mis_recetas_medicamentos',
        title: 'MIS RECETAS Y MEDICAMENTOS',
        description: 'Como ver tus recetas y medicamentos.',
        isEnabled: () => this.isPaciente(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('paciente_mis_recetas_medicamentos')) return;
          this.ensureRoute('/dashboard', () => this.runPacienteMisRecetasMedsTour());
        },
      },
      // PACIENTE · Eventos de salud
      {
        id: 'paciente_eventos_salud',
        title: 'EVENTOS DE SALUD',
        description: 'Como crear y ver tus eventos de salud.',
        isEnabled: () => this.isPaciente(),
        start: (opts) => {
          const force = !!opts?.force;
          if (!force && this.wasTourCompleted('paciente_eventos_salud')) return;
          this.ensureRoute('/patient/health-event-list', () => this.runPacienteEventosSaludTour());
        },
      },

    ];
  }

  // =========================
  // Reglas de visibilidad (roles)
  // =========================
  private canSeeCrearPersonalTour(): boolean {
    return this.isAdmin() || this.isSupervisor() || this.isResponsable();
  }

  // =========================
  // GENERAL HEADER TOUR (usa archivo externo de steps)
  // =========================
  private runGeneralHeaderTour(): void {
    const steps = buildGeneralHeaderSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    if (!steps.length) return;

    this.destroyDriver();
    this.lastStepIndexReached = -1;

    this._driver = driver({
      animate: true,
      allowClose: true,
      overlayOpacity: 0.62,
      stagePadding: 8,
      stageRadius: 14,
      showProgress: true,
      popoverClass: 'rmd-driver-popover',
      onDestroyed: () => {
        this.disconnectProgressObserver();
        if (this.lastStepIndexReached >= steps.length - 1) {
          this.completeTour('tutorial_general');
        }
      },
    });

    steps.forEach((step, idx) => {
      const original = (step as any).onHighlightStarted;
      const elementSel = (step as any).element as string;
      (step as any).onHighlightStarted = () => {
        this.lastStepIndexReached = Math.max(this.lastStepIndexReached, idx);
        if (typeof original === 'function') original();
        setTimeout(() => this.setSpotlightForSelector(elementSel), 0);
        setTimeout(() => this.setSpotlightForSelector(elementSel), 120);
      };
    });

    this._driver.setSteps(steps);
    this._driver.drive();
    setTimeout(() => this.observePopoverProgress(steps.length), 0);
  }

  // =========================================
  // SUPER ADMIN · FILTRO AVANZADO
  // =========================================
  private runSuperAdminAdvancedFilterTour(): void {
    const steps = buildAdminAdvancedFilterSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),
      isParticular: () => this.isParticular(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('admin_filtro_avanzado', steps);
  }

  // =========================================
  // SUPER ADMIN · VER RECETAS
  // =========================================
  private runSuperAdminViewRecipesTour(): void {
    // ETAPA 1: me aseguro estar en Recetas y que haya filas cargadas
    this.ensureRoute('/prescription/prescription-list', () => {
      this.prepareRecipesMonthResults()
        .then(() => {
          // ETAPA 2: ya con DOM listo, ahora si construyo steps (ya incluira acciones)
          const steps = buildAdminViewRecipesSteps({
            elementExists: (s) => this.elementExists(s),

            isAdmin: () => this.isAdmin(),
            isSupervisor: () => this.isSupervisor(),

            openProfileMenu: () => this.tourUi.openProfileMenu(),
            closeProfileMenu: () => this.tourUi.closeProfileMenu(),

            ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

            waitForElement: (sel, t) => this.waitForElement(sel, t),
            moveNextSafe: () => this.moveNextSafe(),

            navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
          });

          this.runStepsAndComplete('admin_ver_recetas', steps);
        })
        .catch(() => {
          // si algo falla, igual intenta arrancar tour (sin romper)
          const steps = buildAdminViewRecipesSteps({
            elementExists: (s) => this.elementExists(s),
            isAdmin: () => this.isAdmin(),
            isSupervisor: () => this.isSupervisor(),
            openProfileMenu: () => this.tourUi.openProfileMenu(),
            closeProfileMenu: () => this.tourUi.closeProfileMenu(),
            ensureSidebarExpanded: () => this.ensureSidebarExpanded(),
            waitForElement: (sel, t) => this.waitForElement(sel, t),
            moveNextSafe: () => this.moveNextSafe(),
            navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
          });

          this.runStepsAndComplete('admin_ver_recetas', steps);
        });
    });
  }

  /**
   * Prepara el listado para que el tour SIEMPRE tenga filas:
   * - Selecciona "MES"
   * - Click en BUSCAR
   * - Espera a que exista al menos una celda de acciones (o una fila)
   */
  private async prepareRecipesMonthResults(): Promise<void> {
    // Espera a que la pantalla haya pintado filtros
    await this.waitForElement('[data-tour="rx-filter-periodo-select"]', 6000);
    await this.waitForElement('[data-tour="rx-filter-btn-buscar"]', 6000);

    // 1) Abrir mat-select (mejor con mousedown para Material)
    const select = this.document.querySelector('[data-tour="rx-filter-periodo-select"]') as HTMLElement | null;
    select?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    select?.click();

    // 2) Seleccionar opcion MES (requiere que ya pusiste data-tour al mat-option de MES)
    await this.waitForElement('[data-tour="rx-filter-periodo-mes"]', 2500);
    const mesOpt = this.document.querySelector('[data-tour="rx-filter-periodo-mes"]') as HTMLElement | null;
    mesOpt?.click();

    // 3) Espera a que Angular procese el selectionChange
    await new Promise((r) => setTimeout(r, 250));

    // 4) Click BUSCAR
    const btn = this.document.querySelector('[data-tour="rx-filter-btn-buscar"]') as HTMLButtonElement | null;
    btn?.click();

    // 5) Esperar filas/acciones. Esto es lo que hace que el tour ya incluya los botones.
    // Lo mas fuerte es rx-actions-cell (solo existe si hay filas).
    await this.waitForElement('[data-tour="rx-actions-cell"]', 8000);

    // (Opcional) un delay extra por si la tabla termina de renderizar iconos/overlays
    await new Promise((r) => setTimeout(r, 200));
  }

  // =========================================
  // ADMIN · ALTA DE CLIENTE (ANTES: PRIMEROS PASOS)
  // =========================================
  private runAdminPrimerosPasosTour(): void {
    const steps = buildAdminOnboardingClientSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('admin_primeros_pasos', steps);
  }

  // =========================================
// ADMIN · CREAR PERSONAL (TOUR INDIVIDUAL)
// =========================================
  private runCrearPersonalTour(): void {
    const steps = buildAdminCrearPersonalSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('admin_crear_personal', steps);
  }

// =========================================
// ADMIN · CREAR SUCURSALES (TOUR INDIVIDUAL)
// =========================================
  private runCrearSucursalesTour(): void {
    const steps = buildAdminCrearSucursalSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('admin_crear_sucursales', steps);
  }

// =========================================
// ADMIN · CREAR GRUPO EMPRESARIAL (TOUR INDIVIDUAL)
// =========================================
  private runCrearGrupoEmpresarialTour(): void {
    const steps = buildAdminCrearGrupoEmpresarialSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('admin_crear_grupo_empresarial', steps);
  }

  private runCrearMedicoTour(): void {
    const steps = buildAdminCrearMedicoSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('admin_crear_medico', steps);
  }

  private runCrearPacienteTour(): void {
    const steps = buildDoctorCrearPacienteSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('doctor_crear_paciente', steps);
  }

  private runCrearRecetaTour(): void {
    const steps = buildDoctorCrearRecetaSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) =>
        this.navigateAndContinue(path, waitSelector),

      finishAndNavigate: (path, waitSelector) =>
        this.finishAndNavigate(path, waitSelector, 'doctor_crear_receta'),
    });

    this.runStepsAndComplete('doctor_crear_receta', steps);
  }

  private runEditarRecetaTour(): void {
    const steps = buildDoctorEditarRecetaSteps({
      elementExists: (s) => this.elementExists(s),

      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),

      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),

      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),

      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),

      navigateAndContinue: (path, waitSelector) =>
        this.navigateAndContinue(path, waitSelector),

      finishAndNavigate: (path, waitSelector) =>
        this.finishAndNavigate(path, waitSelector, 'doctor_editar_receta'),
    });

    this.runStepsAndComplete('doctor_editar_receta', steps);
  }

  private runStaffSurtirRecetaTour(): void {
    const steps = buildStaffSurtirRecetaSteps({
      elementExists: (s) => this.elementExists(s),
      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),
      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),
      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),
      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),
      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('staff_surtir_receta', steps);
  }

  private runPacienteMisRecetasMedsTour(): void {
    const steps = buildPacienteVerRecetasYMedsSteps({
      elementExists: (s) => this.elementExists(s),
      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),
      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),
      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),
      waitForElement: (sel, t) => this.waitForElement(sel, t),
      moveNextSafe: () => this.moveNextSafe(),
      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('paciente_mis_recetas_medicamentos', steps);
  }

  private runPacienteEventosSaludTour(): void {
    const steps = buildPacienteEventosSaludSteps({
      elementExists: (s) => this.elementExists(s),
      isAdmin: () => this.isAdmin(),
      isSupervisor: () => this.isSupervisor(),
      openProfileMenu: () => this.tourUi.openProfileMenu(),
      closeProfileMenu: () => this.tourUi.closeProfileMenu(),
      ensureSidebarExpanded: () => this.ensureSidebarExpanded(),
      waitForElement: (sel, t) => this.waitForElement(sel, t),
      waitForNotExists: (sel, t) => this.waitForNotExists(sel, t),
      moveNextSafe: () => this.moveNextSafe(),
      navigateAndContinue: (path, waitSelector) => this.navigateAndContinue(path, waitSelector),
    });

    this.runStepsAndComplete('paciente_eventos_salud', steps);
  }


  // =========================================================
  // Runner generico: ejecuta pasos y marca tour como completado
  // =========================================================
  private runStepsAndComplete(tourKey: RmdTourId, steps: DriveStep[]): void {
    if (!steps?.length) return;

    this.destroyDriver();
    this.lastStepIndexReached = -1;

    this._driver = driver({
      animate: true,
      allowClose: true,
      overlayOpacity: 0.62,
      stagePadding: 8,
      stageRadius: 14,
      showProgress: true,
      popoverClass: 'rmd-driver-popover',
      onDestroyed: () => {
        this.disconnectProgressObserver();
        if (this.lastStepIndexReached >= steps.length - 1) {
          this.completeTour(tourKey);
        }
      },
    });

    steps.forEach((step, idx) => {
      const original = (step as any).onHighlightStarted;
      const elementSel = (step as any).element as string;
      (step as any).onHighlightStarted = () => {
        this.lastStepIndexReached = Math.max(this.lastStepIndexReached, idx);
        if (typeof original === 'function') original();
        setTimeout(() => this.setSpotlightForSelector(elementSel), 0);
        setTimeout(() => this.setSpotlightForSelector(elementSel), 120);
      };
    });

    this._driver.setSteps(steps);
    this._driver.drive();
    setTimeout(() => this.observePopoverProgress(steps.length), 0);
  }

  // =========================================================
  // NAVIGATION HELPERS (para tours que cambian de pantalla)
  // =========================================================
  private navigateAndContinue(path: string, waitSelector: string): void {
    this.tourUi?.closeProfileMenu?.();

    this.router.navigateByUrl('/' + path, { replaceUrl: true }).then(() => {
      this.waitForElement(waitSelector, 6000).then(() => {
        setTimeout(() => this.moveNextSafe(), 200);
      });
    });
  }

  private waitForElement(selector: string, timeoutMs = 4000): Promise<void> {
    return new Promise((resolve) => {
      const exists = () => !!this.document.querySelector(selector);
      if (exists()) return resolve();

      const obs = new MutationObserver(() => {
        if (exists()) {
          obs.disconnect();
          resolve();
        }
      });

      obs.observe(this.document.body, { childList: true, subtree: true });

      setTimeout(() => {
        obs.disconnect();
        resolve();
      }, timeoutMs);
    });
  }

  private waitForNotExists(selector: string, timeoutMs = 4000): Promise<void> {
    return new Promise((resolve) => {
      const notExists = () => !this.document.querySelector(selector);
      if (notExists()) return resolve();

      const obs = new MutationObserver(() => {
        if (notExists()) {
          obs.disconnect();
          resolve();
        }
      });

      obs.observe(this.document.body, { childList: true, subtree: true });

      setTimeout(() => {
        obs.disconnect();
        resolve();
      }, timeoutMs);
    });
  }


  private moveNextSafe(): void {
    const drv: any = this._driver as any;
    if (drv?.moveNext) drv.moveNext();
  }

  // =========================
  // Navigation helpers
  // =========================
  private ensureRoute(path: string, cb: () => void): void {
    const url = this.router.url || '';
    if (!url.startsWith(path)) {
      this.router.navigateByUrl(path, { replaceUrl: true }).then(() => {
        setTimeout(() => cb(), 250);
      });
      return;
    }
    cb();
  }

  // =========================
  // Progreso en español
  // =========================
  private observePopoverProgress(totalSteps: number): void {
    this.disconnectProgressObserver();

    const popover = this.document.querySelector('.rmd-driver-popover.driver-popover');
    if (!popover) return;

    this.progressObserver = new MutationObserver(() => {
      this.updateProgressToSpanish(totalSteps);
    });

    this.progressObserver.observe(popover, { childList: true, subtree: true });
    this.updateProgressToSpanish(totalSteps);
  }

  private updateProgressToSpanish(totalSteps: number): void {
    const el = this.document.querySelector(
      '.rmd-driver-popover .driver-popover-progress-text'
    ) as HTMLElement | null;

    if (!el) return;

    const raw = (el.textContent || '').trim(); // "2 of 5"
    const m = raw.match(/(\d+)\s*of\s*(\d+)/i);
    if (!m) return;

    const current = Number(m[1]);
    const total = Number(m[2]) || totalSteps;
    el.textContent = `PASO ${current} DE ${total}`;
  }

  private disconnectProgressObserver(): void {
    try { this.progressObserver?.disconnect(); } catch {}
    this.progressObserver = null;
  }

  // =========================
  // Sidebar UI
  // =========================
  private ensureSidebarExpanded(): void {
    const body = this.document.body;
    if (body.classList.contains('side-closed')) {
      body.classList.remove('side-closed');
      body.classList.remove('submenu-closed');
      localStorage.setItem('collapsed_menu', 'false');
    }
    if (body.classList.contains('overlay-open')) {
      body.classList.remove('overlay-open');
    }
  }

  // =========================
  // Persistence
  // =========================
  private getStorageKey(tourKey: string): string {
    const u: any = this.authManagement.userData?.() ?? {};
    const id = u?.IdUsuario || u?.idUsuario || u?.id || 'anon';
    const roleKey = this.getRoleKey();
    return `rmd_tour_${tourKey}_${this.TOUR_VERSION}_${roleKey}_${id}`;
  }

  private wasTourCompleted(tourKey: string): boolean {
    return localStorage.getItem(this.getStorageKey(tourKey)) === 'true';
  }

  private completeTour(tourKey: string): void {
    localStorage.setItem(this.getStorageKey(tourKey), 'true');
  }

  private destroyDriver(): void {
    this.disconnectProgressObserver();
    try { (this._driver as any)?.destroy?.(); } catch {}
    this._driver = null;
  }

  // =========================
  // Rol REAL (tu app)
  // =========================
  private getUserRole(): string {
    try {
      const userData: any = this.authManagement.userData?.() ?? {};
      return userData?.IdTipoUsuario ?? userData?.TipoUsuario ?? userData?.role ?? '';
    } catch {
      return '';
    }
  }

  private isAdmin(): boolean {
    try {
      return this.authManagement.isUserAdmin();
    } catch {
      return false;
    }
  }

  private isSupervisor(): boolean {
    try {
      return this.authManagement.isUserPharmacySupervision();
    } catch {
      return false;
    }
  }

  private isResponsable(): boolean {
    try {
      return this.authManagement.isUserPharmacyResponsible();
    } catch {
      return false;
    }
  }

  private isEmpleado(): boolean {
    try {
      return this.authManagement.isUserPharmacyEmployee();
    } catch {
      return false;
    }
  }

  private isDoctor(): boolean {
    try {
      return this.authManagement.isUserDoctor();
    } catch {
      return false;
    }
  }

  private isParticular(): boolean {
    try {
      return this.authManagement.isUserParticular();
    } catch {
      return false;
    }
  }

  private isPaciente(): boolean {
    try {
      return this.authManagement.isUserPatient();
    } catch {
      return false;
    }
  }

  private getRoleKey(): string {
    if (this.isAdmin()) return 'ADMIN';
    if (this.isSupervisor()) return 'SUPERVISOR';
    if (this.isResponsable()) return 'RESPONSABLE';
    if (this.isEmpleado()) return 'EMPLEADO';
    if (this.isDoctor()) return 'DOCTOR';
    if (this.isPaciente()) return 'PACIENTE';
    return 'USER';
  }

  private canSeeDoctorTours(): boolean {
    return this.isDoctor() || this.isParticular();
  }

  private canSeeBranchFilterTours(): boolean {
    return this.isAdmin() || this.isSupervisor() || this.isParticular();
  }

  private elementExists(selector: string): boolean {
    try { return !!this.document.querySelector(selector); } catch { return false; }
  }

  private setSpotlightForSelector(selector: string): void {
    const root = this.document.documentElement;

    // fallback (centro)
    if (!selector || selector === 'body') {
      root.style.setProperty('--rmd-spot-x', '50vw');
      root.style.setProperty('--rmd-spot-y', '50vh');
      root.style.setProperty('--rmd-spot-r', '220px');
      return;
    }

    const el = this.document.querySelector(selector) as HTMLElement | null;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const x = Math.round(r.left + r.width / 2);
    const y = Math.round(r.top + r.height / 2);

    const base = Math.max(r.width, r.height);
    const radius = Math.min(340, Math.max(180, Math.round(base * 1.25)));

    root.style.setProperty('--rmd-spot-x', `${x}px`);
    root.style.setProperty('--rmd-spot-y', `${y}px`);
    root.style.setProperty('--rmd-spot-r', `${radius}px`);
  }

  private finishAndNavigate(
    path: string,
    waitSelector?: string,
    tourKey?: RmdTourId
  ): void {
    this.tourUi?.closeProfileMenu?.();

    if (tourKey) {
      this.completeTour(tourKey);
    }

    this.destroyDriver();

    this.router.navigateByUrl('/' + path, { replaceUrl: true }).then(() => {
      if (waitSelector) {
        this.waitForElement(waitSelector, 6000);
      }
    });
  }

  private getAdvancedFilterStartRoute(): string {
    if (this.isParticular()) {
      return '/patient/patient-list';
    }

    if (this.isAdmin() || this.isSupervisor()) {
      return '/admin/user-list';
    }

    return '/dashboard';
  }

}
